import { Pills } from '@amelio/ui-library';

import type { ReviewTemplate } from '@/types/review/ReviewTemplate';
import type { LocalizedText } from '@/utils/localization';

import { useAmelioTranslations } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';

interface Template {
    id: string;
    title: LocalizedText;
    description: string;
    questions: number;
}

const toTemplateCard = ({ id, title, description, questions }: ReviewTemplate): Template => ({
    id,
    title,
    description,
    questions: questions?.length ?? 0,
});

interface TemplateSelectorProps {
    templates: Template[];
    amelioTemplates?: ReviewTemplate[];
    selectedTemplateId: string | null;
    onSelectTemplate: (templateId: string) => void;
}

const TemplateCard = ({
    template,
    isSelected,
    onSelect,
}: {
    template: Template;
    isSelected: boolean;
    onSelect: () => void;
}) => {
    const { t } = useAmelioTranslations();

    return (
        <button
            type={'button'}
            onClick={onSelect}
            className={`flex min-h-[210px] w-full flex-col items-center justify-center gap-[10px] rounded-2xl px-9 py-6 transition-all ${
                isSelected
                    ? 'border-primary-500 border bg-white/90 shadow-[0_2px_6px_0_rgba(45,52,57,0.06)]'
                    : 'hover:border-primary border border-transparent bg-white/90'
            }`}
            data-testid={`template-${template.id}`}
        >
            <div className={'flex h-full w-full flex-col items-center justify-center gap-6'}>
                <h4 className={'text-subtitle text-center font-medium text-gray-900'}>
                    {getLocalizedText(template.title)}
                </h4>

                {template.description && (
                    <p
                        className={
                            'flex items-center justify-center text-center text-sm font-medium text-gray-900'
                        }
                    >
                        {template.description}
                    </p>
                )}

                <Pills
                    size={'sm'}
                    variant={'purple'}
                    className={'py-2'}
                >
                    {t('managerAssessmentSetup.questionCount', { count: template.questions })}
                </Pills>
            </div>
        </button>
    );
};

export const TemplateSelector = ({
    templates,
    amelioTemplates,
    selectedTemplateId,
    onSelectTemplate,
}: TemplateSelectorProps) => {
    const { t } = useAmelioTranslations();

    const allTemplates = [...templates, ...(amelioTemplates ?? []).map(toTemplateCard)];

    if (allTemplates.length === 0) {
        return (
            <p
                className={'py-4 text-center text-sm text-gray-500'}
                data-testid={'template-selector-empty'}
            >
                {t('managerAssessmentSetup.noSurveyNote')}
            </p>
        );
    }

    return (
        <div
            className={'grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3'}
            data-testid={'template-selector'}
        >
            {allTemplates.map((template) => (
                <TemplateCard
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplateId === template.id}
                    onSelect={() => onSelectTemplate(template.id)}
                />
            ))}
        </div>
    );
};
